import { useState } from "react";

import dayjs from "dayjs";
import {
  Copy,
  Eye,
  MoreHorizontal,
  Pencil,
  Power,
  PowerOff,
  Trash2,
} from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import type { AccountMappingTemplate } from "~/types/contabilidad";

interface MapeoTemplateTableProps {
  templates: AccountMappingTemplate[];
  isLoading: boolean;
  onView: (template: AccountMappingTemplate) => void;
  onEdit: (template: AccountMappingTemplate) => void;
  onDelete: (id: string) => void;
  onToggleActive: (id: string, isActive: boolean) => void;
  onCopy: (template: AccountMappingTemplate) => void;
}

export function MapeoTemplateTable({
  templates,
  isLoading,
  onView,
  onEdit,
  onDelete,
  onToggleActive,
  onCopy,
}: MapeoTemplateTableProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] =
    useState<AccountMappingTemplate | null>(null);

  const handleDeleteClick = (template: AccountMappingTemplate) => {
    setTemplateToDelete(template);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (templateToDelete) {
      onDelete(templateToDelete.id);
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (templates.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="text-muted-foreground">
          No hay plantillas de mapeo registradas.
        </p>
        <p className="text-sm text-muted-foreground">
          Crea una nueva plantilla para comenzar a mapear cuentas.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="rounded-xl border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nombre</TableHead>
              <TableHead>Descripcion</TableHead>
              <TableHead className="w-[100px] text-center">Cuentas</TableHead>
              <TableHead>Empresa</TableHead>
              <TableHead className="w-[80px]">Estado</TableHead>
              <TableHead>Fecha Creacion</TableHead>
              <TableHead className="w-[70px]">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {templates.map((template, index) => (
              <TableRow
                key={template.id}
                className="cursor-pointer duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both hover:bg-muted/50"
                style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
                onClick={() => onView(template)}
              >
                <TableCell className="font-medium">{template.name}</TableCell>
                <TableCell className="max-w-[200px] truncate text-muted-foreground">
                  {template.description || "-"}
                </TableCell>
                <TableCell className="text-center">
                  <Badge variant="secondary">
                    {template.mappings_count ?? 0}
                  </Badge>
                </TableCell>
                <TableCell>{template.company?.name ?? "-"}</TableCell>
                <TableCell>
                  <Badge
                    variant={template.is_active ? "default" : "secondary"}
                    className={
                      template.is_active
                        ? "bg-green-100 text-green-800"
                        : "bg-gray-100 text-gray-600"
                    }
                  >
                    {template.is_active ? "Activa" : "Inactiva"}
                  </Badge>
                </TableCell>
                <TableCell>
                  {dayjs(template.created_at).format("DD/MM/YYYY")}
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="h-8 w-8 p-0">
                        <span className="sr-only">Abrir menu</span>
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => onView(template)}>
                        <Eye className="mr-2 h-4 w-4" />
                        Ver Mapeos
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onEdit(template)}>
                        <Pencil className="mr-2 h-4 w-4" />
                        Editar
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onCopy(template)}>
                        <Copy className="mr-2 h-4 w-4" />
                        Duplicar
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() =>
                          onToggleActive(template.id, !template.is_active)
                        }
                      >
                        {template.is_active ? (
                          <>
                            <PowerOff className="mr-2 h-4 w-4" />
                            Desactivar
                          </>
                        ) : (
                          <>
                            <Power className="mr-2 h-4 w-4" />
                            Activar
                          </>
                        )}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => handleDeleteClick(template)}
                        className="text-destructive"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Eliminar
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar plantilla?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta accion eliminara la plantilla "{templateToDelete?.name}" y
              todos sus mapeos asociados. Esta accion no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
